import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Ungültiger Reset-Link');
      navigate('/login');
      return;
    }

    verifyToken();
  }, [token]);

  useEffect(() => {
    // Check password strength as user types
    const checkStrength = async () => {
      if (newPassword.length > 0) {
        try {
          const response = await axios.post(`${API}/api/auth/check-password-strength`, {
            password: newPassword,
            role: 'admin' // Default to admin rules
          });
          setPasswordValidation(response.data);
        } catch (error) {
          console.error('Error checking password strength:', error);
        }
      } else {
        setPasswordValidation(null);
      }
    };

    const debounce = setTimeout(checkStrength, 300);
    return () => clearTimeout(debounce);
  }, [newPassword]);

  const verifyToken = async () => {
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/api/auth/verify-reset-token`, {
        token
      });
      setTokenValid(true);
      setUserEmail(response.data.email);
    } catch (error) {
      toast.error('Reset-Link ist ungültig oder abgelaufen');
      setTokenValid(false);
      setTimeout(() => navigate('/forgot-password'), 2000);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (passwordValidation && !passwordValidation.valid) {
      toast.error('Bitte erfüllen Sie alle Passwortanforderungen');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });

      setSuccess(true);
      toast.success('Passwort erfolgreich zurückgesetzt!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      const detail = error.response?.data?.detail;
      
      if (detail?.errors) {
        detail.errors.forEach(err => toast.error(err));
      } else {
        toast.error(detail || 'Fehler beim Zurücksetzen des Passworts');
      }
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Link wird überprüft...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2 text-red-600">
              <XCircle className="h-6 w-6" />
              Ungültiger Link
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Der Reset-Link ist ungültig oder abgelaufen.
            </p>
            <Link to="/forgot-password">
              <Button className="w-full">
                Neuen Link anfordern
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url('https://images.unsplash.com/photo-1632671414008-d3ea7df1dc4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnYXMlMjBzdGF0aW9uJTIwZXh0ZXJpb3IlMjBuaWdodHxlbnwwfHx8fDE3NjU4MzM3Nzh8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              Passwort zurückgesetzt!
            </CardTitle>
            <CardDescription className="text-center">
              Sie können sich jetzt mit Ihrem neuen Passwort anmelden
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button className="w-full">
                Zur Anmeldung
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url('https://images.unsplash.com/photo-1632671414008-d3ea7df1dc4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnYXMlMjBzdGF0aW9uJTIwZXh0ZXJpb3IlMjBuaWdodHxlbnwwfHx8fDE3NjU4MzM3Nzh8MA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <img src="/allflowx-horizontal-light.png" alt="AllFlowX" className="h-24 w-auto object-contain dark:hidden" />
            <img src="/allflowx-horizontal-dark.png" alt="AllFlowX" className="h-24 w-auto object-contain hidden dark:block" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            Neues Passwort festlegen
          </CardTitle>
          <CardDescription className="text-center">
            für {userEmail}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>
              
              <PasswordStrengthIndicator 
                password={newPassword} 
                role="admin" 
                validation={passwordValidation}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-600">Passwörter stimmen nicht überein</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !passwordValidation?.valid || newPassword !== confirmPassword}
            >
              {loading ? 'Wird gespeichert...' : 'Passwort zurücksetzen'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link 
              to="/login" 
              className="text-sm text-primary hover:underline"
            >
              Zurück zur Anmeldung
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
